import { useCallback, useEffect, useMemo, useState } from "react";

import reportsService from "../api/reports.service";
import {
  ExportFormat,
  ReportOptions,
  ReportRow,
  ReportsData,
  ReportsFilters,
  ReportTab,
} from "../types";

const emptyReports: ReportsData = {
  summary: {
    inventoryValue: 0,
    totalStock: 0,
    lowStock: 0,
    purchaseCost: 0,
    openTickets: 0,
    suppliers: 0,
  },
  inventoryReport: [],
  purchaseReport: [],
  maintenanceReport: [],
  supplierReport: [],
  vehicleReport: [],
  spareReturnReport: [],
  lowStockReport: [],
};

const emptyOptions: ReportOptions = {
  hubs: [],
  categories: [],
  suppliers: [],
};

export const useReports = () => {
  const [reports, setReports] = useState<ReportsData>(emptyReports);
  const [options, setOptions] = useState<ReportOptions>(emptyOptions);
  const [filters, setFilters] = useState<ReportsFilters>({
    reportTab: "inventory",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = useCallback(
    async (nextFilters: ReportsFilters = filters) => {
      try {
        setLoading(true);
        setError(null);
        setReports(await reportsService.getReports(nextFilters));
      } catch (err) {
        console.error(err);
        setError("Failed to load reports.");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    reportsService
      .getOptions()
      .then(setOptions)
      .catch((err) => console.error(err));
  }, []);

  const updateFilters = (nextFilters: Partial<ReportsFilters>) => {
    const updated = {
      ...filters,
      ...nextFilters,
    };

    setFilters(updated);
    fetchReports(updated);
  };

  const activeRows = useMemo<ReportRow[]>(() => {
    switch (filters.reportTab) {
      case "purchase":
        return reports.purchaseReport;
      case "maintenance":
        return reports.maintenanceReport;
      case "supplier":
        return reports.supplierReport;
      case "vehicle":
        return reports.vehicleReport;
      case "spareReturn":
        return reports.spareReturnReport;
      case "lowStock":
        return reports.lowStockReport;
      case "inventory":
      default:
        return reports.inventoryReport;
    }
  }, [filters.reportTab, reports]);

  const exportReport = (format: ExportFormat, tab: ReportTab = filters.reportTab || "inventory") => {
    reportsService.exportRows(activeRows, tab, format);
  };

  return {
    reports,
    options,
    filters,
    activeRows,
    loading,
    error,
    refresh: fetchReports,
    updateFilters,
    exportReport,
  };
};

export default useReports;
